import { useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { GenerationSettings } from "../GenerationSettings";
import { Button } from "../ui/button";
import { ExpandingWorkspace } from "./ExpandingWorkspace";
import { PendingTopicData } from "./TopicCard";
import { BrandKit } from "../../models/BrandKit";
import { PlatformConnection } from "../../models/Platform";
import { IdeaGenerationSettings } from "../../models/Autopilot";
import { VisualStyleId } from "../../constants/visualStyles";

export function IdeaGenerationWorkspace({
  topic,
  defaults,
  notice,
  brandKits,
  connections,
  onGenerate,
  onClose,
}: {
  topic: PendingTopicData;
  defaults: IdeaGenerationSettings;
  notice?: string;
  brandKits: BrandKit[];
  connections: PlatformConnection[];
  onGenerate: (settings: IdeaGenerationSettings) => Promise<{ postId: string }>;
  onClose: () => void;
}) {
  const [brandKitId, setBrandKitId] = useState(defaults.brandKitId);
  const [visualStyleId, setVisualStyleId] = useState<VisualStyleId>(
    defaults.visualStyleId,
  );
  const [connectionIds, setConnectionIds] = useState(
    defaults.connectionIds.filter((id) => connections.some((c) => c.id === id)),
  );
  const [templateIds, setTemplateIds] = useState(new Set(defaults.templateIds));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const title =
    topic.content.category === "listicle"
      ? topic.content.content.title
      : topic.content.content.quote;
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onGenerate({
        brandKitId,
        visualStyleId,
        connectionIds,
        templateIds: [...templateIds],
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn’t start generation. Please try again.",
      );
      setSubmitting(false);
    }
  };
  return (
    <ExpandingWorkspace
      expanded
      motion="fade"
      dismissible={!submitting}
      onClose={onClose}
      title="Generate visuals"
      description="Customize the visuals for this idea. Escape returns to Ideas."
      backLabel="Back to Ideas"
    >
      <form
        className="mx-auto max-w-3xl px-4 py-8 sm:px-8 sm:py-12"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset className="min-w-0" disabled={submitting}>
          <p className="text-sm text-muted-foreground">
            {topic.content.category === "listicle" ? "Carousel" : "Quote post"}{" "}
            · {topic.sourceName}
          </p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
            {title}
          </h1>
          {!notice && (
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Choose how to bring this idea to life. We’ll save these settings
              for this source.
            </p>
          )}
          <GenerationSettings
            userBrandKits={brandKits}
            brandKitNotice={
              notice && (
                <div
                  role="status"
                  className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50"
                >
                  <Info
                    aria-hidden="true"
                    className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300"
                  />
                  <div>
                    <p className="text-base font-semibold">
                      Review your settings to continue
                    </p>
                    <p className="mt-1 text-sm leading-relaxed">{notice}</p>
                  </div>
                </div>
              )
            }
            sourceMayHaveImages={
              !!topic.extractedArticleImages?.some((image) => image.storagePath)
            }
            selectedBrandKitId={brandKitId}
            setSelectedBrandKitId={setBrandKitId}
            selectedVisualStyleId={visualStyleId}
            setSelectedVisualStyleId={setVisualStyleId}
            activeConnections={connections}
            selectedConnectionIds={connectionIds}
            setSelectedConnectionIds={setConnectionIds}
            selectedTemplateIds={templateIds}
            setSelectedTemplateIds={setTemplateIds}
            templateCategory={topic.content.category}
            // Ideas become drafts; where they post is chosen when publishing.
            showPostingTo={false}
          />
          <div className="mt-10 border-t border-border/60 pt-6">
            {error && (
              <p role="alert" className="mb-4 text-sm text-destructive">
                {error}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                1 draft · 1 credit. You review before publishing.
              </p>
              <Button
                type="submit"
                variant="brand"
                size="lg"
                disabled={submitting}
              >
                {submitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                )}
                {submitting ? "Starting your generation…" : "Generate Visuals"}
              </Button>
            </div>
          </div>
        </fieldset>
      </form>
    </ExpandingWorkspace>
  );
}
